import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, User, Loader2 } from "lucide-react";
import AuthShell from "../components/AuthShell";
import GoogleIcon from "../components/GoogleIcon";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../context/AuthContext";

const GOOGLE_ERRORS = {
  "email-registered":
    "This email is already registered with email/password. Please sign in with your password.",
  "google-auth-failed": "Google authentication failed. Please try again.",
};

export default function SignUp() {
  const { signUpWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ fullName: "", email: "", password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const err = searchParams.get("error");
    if (err && GOOGLE_ERRORS[err]) {
      setError(GOOGLE_ERRORS[err]);
    }
  }, [searchParams]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.fullName || !form.email || !form.password || !form.confirmPassword) {
      setError("Please fill in all fields");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const data = await signUpWithEmail(form);
      if (data?.resumed) {
        // User already verified their email on a previous session but
        // didn't finish phone verification. Jump straight to /phone.
        navigate("/phone");
      } else {
        navigate("/verify");
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    setGoogleLoading(true);
    signInWithGoogle({ isSignup: true });
    // Browser navigates away — spinner stays visible until page unloads.
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Manage properties, tenants and rent with ease."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/signin" className="font-medium text-indigo-600 hover:text-indigo-700">
            Sign in
          </Link>
          <br />
          <span className="mt-2 inline-block text-xs text-gray-400">
            By signing up you agree to our{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Privacy Policy</a>
          </span>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="fullName" className="label-base">
            Full name
          </label>
          <div className="relative">
            <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              id="fullName"
              type="text"
              autoComplete="name"
              value={form.fullName}
              onChange={update("fullName")}
              placeholder="Jane Doe"
              className="input-base pl-9"
            />
          </div>
        </div>
        <div>
          <label htmlFor="email" className="label-base">
            Email
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@company.com"
              className="input-base pl-9"
            />
          </div>
        </div>
        <div>
          <label htmlFor="password" className="label-base">
            Password
          </label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            value={form.password}
            onChange={update("password")}
            placeholder="At least 8 characters"
          />
        </div>
        <div>
          <label htmlFor="confirmPassword" className="label-base">
            Confirm password
          </label>
          <PasswordInput
            id="confirmPassword"
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={update("confirmPassword")}
            placeholder="Re-enter password"
          />
        </div>
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <button type="submit" className="btn-primary w-full" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Create account
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Or
        </span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <button
        onClick={onGoogle}
        className="btn-secondary w-full"
        disabled={googleLoading}
      >
        {googleLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleIcon className="h-5 w-5" />
        )}
        Continue with Google
      </button>
    </AuthShell>
  );
}
